/**
 * CORP//LLM — In-Game Chat Panel
 * Ephemeral chat via POST /api/sessions/{id}/chat + WS chat_message events.
 * Renders as a slide-in panel, toggled via chat button in top bar.
 */

import { state } from './main';
import { getJwt, parseJwt } from './auth';
import { sendChat, getChatHistory } from './api';
import type { ChatMessage } from './api';
import type { GameSocket } from './ws';

// ── Local message buffer (ephemeral, not persisted) ────────────────────────

const messages: ChatMessage[] = [];
const MAX_MESSAGES = 200;

let panelEl: HTMLElement | null = null;
let listEl: HTMLElement | null = null;

// ── Init ────────────────────────────────────────────────────────────────────

/**
 * Wire up chat: listen for WS chat_message events.
 * Call once after GameSocket is connected.
 */
export function initChat(socket: GameSocket): void {
  socket.on<ChatMessage>('chat_message', (msg) => {
    if (!msg) return;
    messages.push(msg);
    if (messages.length > MAX_MESSAGES) messages.shift();
    appendMessageToDOM(msg);
  });
}

// ── Panel rendering ─────────────────────────────────────────────────────────

export function createChatPanel(options?: { embedded?: boolean }): HTMLElement {
  const embedded = options?.embedded ?? false;
  const root = document.createElement('div');
  root.id = embedded ? 'chatPanelEmbed' : 'chatPanel';
  root.className = embedded ? 'chat-panel chat-embedded' : 'chat-panel';
  root.innerHTML = `
    <style>
      .chat-panel {
        position: fixed; top: 48px; bottom: 0; width: min(360px, 85vw);
        right: 0; transform: translateX(100%);
        background: var(--bg-panel, #0d1117); border-left: 1px solid rgba(255,255,255,0.08);
        display: flex; flex-direction: column; z-index: 900;
        transition: right 0.2s ease; font-family: var(--mono);
      }
      .chat-panel.open { transform: translateX(0); }
      .chat-panel.chat-embedded {
        position: relative; top: auto; bottom: auto; right: auto;
        transform: none; width: 100%; height: 100%; z-index: auto;
        border-left: none; border: 1px solid rgba(255,255,255,0.08);
        transition: none;
      }
      .chat-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.08);
        font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.15em;
      }
      .chat-close { background: none; border: none; color: var(--text-dim); cursor: pointer; font-size: 16px; }
      .chat-messages {
        flex: 1; overflow-y: auto; padding: 8px 14px; display: flex; flex-direction: column; gap: 6px;
      }
      .chat-msg { font-size: 12px; line-height: 1.5; }
      .chat-msg-name { color: var(--cyan); font-size: 11px; }
      .chat-msg-corp { color: var(--text-dim); font-size: 12px; margin-left: 4px; }
      .chat-msg-time { color: var(--text-dim); font-size: 12px; float: right; }
      .chat-msg-text { color: var(--text-mid); word-break: break-word; }
      .chat-msg.self .chat-msg-name { color: var(--green); }
      .chat-input-wrap {
        display: flex; gap: 6px; padding: 10px 14px; border-top: 1px solid rgba(255,255,255,0.08);
      }
      .chat-input {
        flex: 1; background: none; border: 1px solid rgba(255,255,255,0.12); color: var(--text-hi);
        padding: 6px 10px; font-family: var(--mono); font-size: 12px; outline: none;
      }
      .chat-input:focus { border-color: var(--cyan); }
      .chat-send {
        padding: 6px 14px; background: rgba(var(--ac),0.1); border: 1px solid rgba(var(--ac),0.3);
        color: var(--cyan); cursor: pointer; font-family: var(--mono); font-size: 11px;
        text-transform: uppercase; letter-spacing: 0.1em;
      }
      .chat-send:hover { background: rgba(var(--ac),0.18); }
      .chat-send:disabled { opacity: 0.4; cursor: default; }
    </style>
    <div class="chat-header">
      <span>SESSION CHAT</span>
      ${embedded ? '' : '<button class="chat-close" id="chatClose">✕</button>'}
    </div>
    <div class="chat-messages" id="chatMessages"></div>
    <div class="chat-input-wrap">
      <input class="chat-input" id="chatInput" placeholder="Nachricht..." maxlength="500" autocomplete="off" />
      <button class="chat-send" id="chatSend">SEND</button>
    </div>
  `;

  // Embedded panels share the same message list reference so WS messages appear
  panelEl = root;
  listEl = root.querySelector('#chatMessages') as HTMLElement;

  // Render existing messages that arrived over WS since boot
  for (const msg of messages) appendMessageToDOM(msg);

  // v1.8.2: fetch persisted history the first time the panel mounts for a
  // given session. Without this the user loses every message on reload.
  // Older servers return 404 on this endpoint — catch it and degrade
  // silently to the previous WS-only behaviour.
  const sid = state.sessionId;
  if (sid && messages.length === 0) {
    getChatHistory(sid, 100).then(res => {
      for (const m of res.messages) {
        const asMsg: ChatMessage = {
          sessionId: sid,
          playerId: m.playerId,
          playerName: m.playerName,
          corpName: m.corpName,
          message: m.message,
          timestamp: m.timestamp,
        };
        messages.push(asMsg);
        appendMessageToDOM(asMsg);
      }
    }).catch(() => { /* 404 on older servers / auth missing — no-op */ });
  }

  // Close button (only for non-embedded panel)
  const closeBtn = root.querySelector('#chatClose');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      root.classList.remove('open');
    });
  }

  // Send
  const input = root.querySelector('#chatInput') as HTMLInputElement;
  const sendBtn = root.querySelector('#chatSend') as HTMLButtonElement;

  async function doSend(): Promise<void> {
    const text = input.value.trim();
    if (!text || !state.sessionId) return;
    input.value = '';
    sendBtn.disabled = true;
    try {
      await sendChat(state.sessionId, text);
    } catch {
      // Re-insert text on failure so user can retry
      input.value = text;
    } finally {
      sendBtn.disabled = false;
      input.focus();
    }
  }

  sendBtn.addEventListener('click', doSend);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSend(); });

  return root;
}

export function toggleChat(): void {
  panelEl?.classList.toggle('open');
  if (panelEl?.classList.contains('open')) {
    const input = panelEl.querySelector('#chatInput') as HTMLInputElement | null;
    input?.focus();
  }
}

// ── DOM helpers ─────────────────────────────────────────────────────────────

function appendMessageToDOM(msg: ChatMessage): void {
  if (!listEl) return;

  const jwt = getJwt();
  const myId = jwt ? parseJwt(jwt)?.sub : null;
  const isSelf = msg.playerId === myId;

  const el = document.createElement('div');
  el.className = 'chat-msg' + (isSelf ? ' self' : '');

  const time = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';

  el.innerHTML = `
    <div>
      <span class="chat-msg-name">${escHtml(msg.playerName)}</span>
      <span class="chat-msg-corp">[${escHtml(msg.corpName)}]</span>
      <span class="chat-msg-time">${time}</span>
    </div>
    <div class="chat-msg-text">${escHtml(msg.message)}</div>
  `;

  listEl.appendChild(el);
  listEl.scrollTop = listEl.scrollHeight;
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
